import { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { Sparkles, LogOut, Loader2, Target, Save, Baby, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const MAX_GOALS = 3;

function daysAgo(iso) {
  if (!iso) return null;
  try {
    const then = new Date(iso).getTime();
    const now = Date.now();
    return Math.floor((now - then) / (1000 * 60 * 60 * 24));
  } catch { return null; }
}

/**
 * PFP-only therapist home. Every child at their school with a simple
 * 3-line goal editor (free-text priorities that steer the LLM daily idea).
 * Weekly cadence — a soft "stale" nudge shows after 7 days.
 */
export default function PfpTherapistHome() {
  const navigate = useNavigate();
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState({});   // child_id → [line1, line2, line3]
  const [saving, setSaving] = useState({});   // child_id → bool

  const authHdr = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  useEffect(() => { fetchChildren(); }, []);

  const fetchChildren = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/pfp-therapist/children`, authHdr());
      const list = r.data?.children || [];
      setChildren(list);
      const d = {};
      list.forEach((c) => {
        const g = c.pfp_priority_goals || [];
        d[c.id] = [g[0] || '', g[1] || '', g[2] || ''];
      });
      setDrafts(d);
    } catch (err) {
      if (err?.response?.status === 403) {
        toast.error('PFP-only therapist access');
        navigate('/');
      } else {
        toast.error('Failed to load children');
      }
    } finally { setLoading(false); }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const setLine = (childId, idx, val) => {
    setDrafts((d) => ({
      ...d,
      [childId]: (d[childId] || ['', '', '']).map((v, i) => (i === idx ? val : v)),
    }));
  };

  const save = async (child) => {
    const goals = (drafts[child.id] || []).map((s) => s.trim()).filter(Boolean).slice(0, MAX_GOALS);
    setSaving((s) => ({ ...s, [child.id]: true }));
    try {
      const r = await axios.put(
        `${API}/pfp-therapist/children/${child.id}/goals`,
        { goals }, authHdr(),
      );
      toast.success(`Weekly goals saved for ${child.name}`);
      // Update the local child row
      setChildren((cs) => cs.map((c) => c.id === child.id
        ? { ...c, pfp_priority_goals: r.data.pfp_priority_goals,
            pfp_priority_goals_updated_at: r.data.pfp_priority_goals_updated_at }
        : c
      ));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save');
    } finally {
      setSaving((s) => ({ ...s, [child.id]: false }));
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <nav className="bg-white border-b border-stone-100 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h1 className="text-xl font-serif text-primary-900">Weekly Priority Goals</h1>
              <p className="text-xs text-stone-500">
                PlayFirst Parent · steer each family's daily ideas
              </p>
            </div>
          </div>
          <Button variant="ghost" onClick={logout} className="rounded-full" data-testid="logout-btn">
            <LogOut className="w-4 h-4 mr-2" /> Logout
          </Button>
        </div>
      </nav>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin text-stone-400" />
          </div>
        ) : children.length === 0 ? (
          <Card className="p-12 text-center rounded-3xl border-stone-100">
            <Baby className="w-12 h-12 mx-auto mb-3 text-stone-300" />
            <p className="text-stone-500">
              No families at your school yet. Once your school admin invites
              parents, their children will appear here for you to set goals.
            </p>
          </Card>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            <Card className="p-4 rounded-2xl bg-primary/5 border-primary/10">
              <div className="flex items-start gap-3">
                <Target className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                <div className="text-sm text-stone-700">
                  <p className="font-medium text-primary-900 mb-1">Set 3 weekly priority goals per child</p>
                  Type them in plain English — short is best. The AI daily play idea
                  will gently steer toward these all week. Just refresh weekly.
                </div>
              </div>
            </Card>

            {children.map((c) => {
              const age = daysAgo(c.pfp_priority_goals_updated_at);
              const stale = age !== null && age >= 7;
              const isDraftDifferent = (drafts[c.id] || []).join('\n').trim() !==
                (c.pfp_priority_goals || []).join('\n').trim();
              return (
                <Card key={c.id} className="p-5 md:p-6 rounded-3xl border-stone-100 bg-white" data-testid={`child-card-${c.id}`}>
                  <div className="flex flex-col md:flex-row md:items-start justify-between gap-3 mb-4">
                    <div>
                      <h3 className="text-lg font-serif text-primary-900">{c.name}</h3>
                      <p className="text-xs text-stone-500">
                        {c.age_years != null && `${c.age_years} yrs · `}
                        Family: {c.parent_name || c.parent_email}
                      </p>
                    </div>
                    <div className="text-right text-xs">
                      {age === null ? (
                        <span className="inline-flex items-center gap-1 text-stone-400">
                          Never set
                        </span>
                      ) : stale ? (
                        <span className="inline-flex items-center gap-1 text-amber-700 bg-amber-50 border border-amber-200 px-2 py-1 rounded-full">
                          <AlertCircle className="w-3 h-3" /> {age}d ago — refresh this week
                        </span>
                      ) : (
                        <span className="text-emerald-700">Updated {age}d ago</span>
                      )}
                    </div>
                  </div>

                  <div className="space-y-2">
                    {[0, 1, 2].map((idx) => (
                      <div key={idx} className="flex items-start gap-2">
                        <span className="text-primary/50 font-serif text-lg leading-10 flex-shrink-0 w-6 text-center">
                          {idx + 1}.
                        </span>
                        <Textarea
                          value={(drafts[c.id] || [])[idx] || ''}
                          onChange={(e) => setLine(c.id, idx, e.target.value)}
                          placeholder={
                            idx === 0
                              ? 'e.g. Encourage 2-word combinations at snack time'
                              : idx === 1
                                ? 'e.g. Model 5 core words during play'
                                : 'e.g. Acknowledge delayed echolalia positively'
                          }
                          rows={2}
                          className="rounded-xl resize-none"
                          data-testid={`goal-input-${c.id}-${idx}`}
                          maxLength={240}
                        />
                      </div>
                    ))}
                  </div>

                  <div className="flex justify-end mt-3">
                    <Button
                      onClick={() => save(c)}
                      disabled={saving[c.id] || !isDraftDifferent}
                      className="rounded-full"
                      data-testid={`save-goals-${c.id}`}
                    >
                      {saving[c.id] ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                      Save this week's goals
                    </Button>
                  </div>
                </Card>
              );
            })}
          </motion.div>
        )}
      </main>
    </div>
  );
}
